import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import {
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
} from "@opentelemetry/sdk-trace-base";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { openobserveRum } from "@openobserve/browser-rum";
import { openobserveLogs } from "@openobserve/browser-logs";

const SERVICE_NAME = "sked-link-admin";
const RUM_SITE = "ooingest.skedsocial.com";
const APP_VERSION = import.meta.env.VITE_APP_VERSION || "unknown";

export function initObservability() {
  if (import.meta.env.VITE_OTEL_ENABLED !== "true") {
    console.info("[Observability] disabled");
    return;
  }

  const environment = import.meta.env.MODE || "development";
  const sampleRate = parseFloat(import.meta.env.VITE_OTEL_SAMPLE_RATE || "0");

  // --- OpenTelemetry traces ---
  const exporter = new OTLPTraceExporter({
    url: import.meta.env.VITE_OTEL_ENDPOINT,
    headers: {
      Authorization: import.meta.env.VITE_OTEL_AUTH_HEADER,
      organization: import.meta.env.VITE_OTEL_ORG,
      "stream-name": import.meta.env.VITE_OTEL_STREAM,
    },
  });

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: SERVICE_NAME,
      "deployment.environment.name": environment,
    }),
    sampler: new TraceIdRatioBasedSampler(sampleRate),
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  provider.register();

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [/\.skedsocial\.com/, /\.sked\.link/],
        ignoreNetworkEvents: true,
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: [/\.skedsocial\.com/, /\.sked\.link/],
        ignoreNetworkEvents: true,
      }),
      new DocumentLoadInstrumentation(),
    ],
  });

  console.log(
    `[Observability] OpenTelemetry initialized (sample rate: ${sampleRate * 100}%)`
  );

  // --- OpenObserve RUM ---
  const rumClientToken = import.meta.env.VITE_RUM_CLIENT_TOKEN;
  const rumOrg = import.meta.env.VITE_RUM_ORG;
  const rumSessionSampleRate = parseInt(
    import.meta.env.VITE_RUM_SESSION_SAMPLE_RATE || "100",
    10
  );
  const rumSessionReplaySampleRate = parseInt(
    import.meta.env.VITE_RUM_SESSION_REPLAY_SAMPLE_RATE || "0",
    10
  );

  openobserveRum.init({
    applicationId: SERVICE_NAME,
    clientToken: rumClientToken,
    site: RUM_SITE,
    organizationIdentifier: rumOrg,
    service: SERVICE_NAME,
    env: environment,
    version: APP_VERSION,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    apiVersion: "v1",
    insecureHTTP: false,
    defaultPrivacyLevel: "mask-user-input",
    sessionSampleRate: rumSessionSampleRate,
    sessionReplaySampleRate: rumSessionReplaySampleRate,
  });

  // --- OpenObserve Logs ---
  openobserveLogs.init({
    clientToken: rumClientToken,
    site: RUM_SITE,
    organizationIdentifier: rumOrg,
    service: SERVICE_NAME,
    env: environment,
    version: APP_VERSION,
    forwardErrorsToLogs: true,
    insecureHTTP: false,
    apiVersion: "v1",
  });

  openobserveRum.startSessionReplayRecording();

  console.log("[Observability] OpenObserve RUM & Logs initialized");
}
